import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitPath} from '@github-ui/paths'
import {Link, RelativeTime} from '@primer/react'
import type {ReactNode} from 'react'

import {useHeaderPageData} from '../../../../page-data/loaders/use-header-page-data'

/**
 * Renders a sentence describing who merged a pull request, the merge commit, and when.
 * Any content passed as `trailing` is placed inside the same inline-flex container so
 * additional sentences (e.g. a follow-up "Delete the branch to tidy up.") stay visually
 * aligned with the merger avatar.
 */
export function MergedDescription({trailing}: {trailing?: ReactNode}) {
  const {
    data: {pullRequest, repository},
  } = useHeaderPageData()
  const {mergedBy, mergedByName, mergedByAvatarUrl, mergedTime, mergeCommitSha} = pullRequest
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const relativeMergedTime = mergedTime ? <RelativeTime datetime={mergedTime} /> : null

  const mergerLink = mergedBy ? (
    <Link
      inline
      className="Link--inTextBlock fgColor-default d-inline-flex flex-items-center gap-1"
      href={`/${mergedBy}`}
      {...getHovercardAttributesForType('user', {login: mergedBy})}
    >
      {mergedByAvatarUrl && <GitHubAvatar src={mergedByAvatarUrl} alt="" size={16} />}
      {mergedBy || mergedByName}
    </Link>
  ) : null

  const commitLink =
    mergeCommitSha && repository?.name ? (
      <Link
        inline
        className="Link--inTextBlock text-mono"
        href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: mergeCommitSha})}
      >
        {mergeCommitSha.slice(0, 7)}
      </Link>
    ) : null

  if (!mergerLink && !commitLink) {
    return (
      <span className="d-inline-flex flex-items-center flex-wrap gap-1">
        <span>
          This pull request was merged
          {relativeMergedTime ? <> {relativeMergedTime}</> : null}
          <span>.</span>
        </span>
        {trailing}
      </span>
    )
  }

  return (
    <span className="d-inline-flex flex-items-center flex-wrap gap-1">
      <span>
        {mergerLink ? (
          <>
            {mergerLink} merged
            {commitLink ? <> in {commitLink}</> : null}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span>.</span>
          </>
        ) : (
          <>
            Merged in {commitLink}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span>.</span>
          </>
        )}
      </span>
      {trailing}
    </span>
  )
}
